// @flow
import React from "react";

import { action, comparer, observable, reaction, toJS } from "mobx";
import { observer } from "mobx-react";

import equals from "fast-deep-equal";
import { withTranslation } from "react-i18next";
import jexl from "jexl";
import classNames from "classnames";
import { parametarExtractor } from "../utils/parametarExtractor.js";
import { getComponentConfig } from "./CustomList/utils/componentConfig.js";
import { translate } from "sulu-admin-bundle/utils/Translator";
import MultiItemSelection1 from "../components/MultiSelection/override/MultiSelection";
import CustomStore from "../misc/override/CustomStore";
import MultiListOverlay from "sulu-admin-bundle/containers/MultiListOverlay";
import multiSelectionStyles from "./CustomStyle.scss";
import MultiItemRenderer from "./CustomList/MultiItemRenderer.js";

import { ResourceRequester } from "sulu-admin-bundle/services/index.js";

import { PlayerUpdater } from "../services/updaters/PlayerUpdater.js";
import { StaffUpdater } from "../services/updaters/StaffUpdater.js";
import { UmpireUpdater } from "../services/updaters/UmpireUpdater.js";
import TeamService from "./CustomList/services/TeamService.js";

import Modal from "../components/CustomList/PassportModal.js";

const submitDelay = 1500;

const MultiSelectionStore = CustomStore;

type Props = {|
    adapter: string,
    allowDeselectForDisabledItems: boolean,
    disabled: boolean,
    disabledIds: Array<string | number>,
    displayProperties: Array<string>,
    icon: string,
    itemDisabledCondition?: ?string,
    label?: string,
    listKey: string,
    locale?: ?IObservableValue<string>,
    onChange: (selectedIds: Array<string | number>) => void,
    onItemClick?: (id: number | string, item: Object) => void,
    options: Object,
    overlayTitle: string,
    resourceKey: string,
    sortable: boolean,
    value: Array<string | number>,
|};

//MultiSelection
@observer
class CustomList extends React.Component<Props> {
    static defaultProps = {
        allowDeselectForDisabledItems: false,
        disabled: false,
        disabledIds: [],
        displayProperties: [],
        icon: "su-plus",
        options: {},
        sortable: true,
        value: [],
    };

    selectionStore: MultiSelectionStore<string | number>;
    changeSelectionDisposer: () => *;
    changeOptionsDisposer: () => *;

    @observable overlayOpen: boolean = false;
    @observable swapPlayer = null;
    @observable passportCache = {};

    constructor(props: Props) {
        super(props);

        const componentCheck = props.schemaOptions["component"]?.value || "";

        const { options, value, dataPath } = props;
        this.locale = this.props.router.attributes.locale || undefined;

        const resourceKey = props.fieldTypeOptions?.resource_key;

        this.startingPlayers = this.props.data.numberOfStartingPlayers;
        // TODO instead of creating the store here and passing the props required for this, we should pass a store prop
        let ids = toJS(value);
        if (typeof ids === "object" && !Array.isArray(ids) && ids !== null) {
            ids = Object.values(ids);
        }
        ids = [...new Set(ids)];

        this.selectionStore = new MultiSelectionStore(
            resourceKey,
            ids,
            this.locale,
            "ids",
            options,
            props.data
        );

        //isCaptain
        this.players = this.selectionStore.items.toJS();
        this.changeSelectionDisposer = reaction(
            () => this.selectionStore.items.map((item) => item.id),
            (loadedItemIds: Array<string | number>) => {
                const { onChange, value } = props;

                if (!equals(toJS(value), toJS(loadedItemIds))) {
                    onChange(loadedItemIds);
                }
            }
        );

        this.changeOptionsDisposer = reaction(
            () => props.options,
            (options) => {
                this.selectionStore.setRequestParameters(options);
                this.selectionStore.loadItems(props.value);
            },
            { equals: comparer.structural }
        );

        this.timeouts = [];

        const config = getComponentConfig(componentCheck, dataPath, props);

        this.header = config.header;
        this.inputField = config.inputField;
        this.columnCustom = config.columnCustom;
        this.footer = config.footer;

        this.state = {
            roles: [],
            data: props.data,
            allMatchPlayers: props.data.allMatchPlayers,
            allTeamPlayers: props.data.allTeamPlayers,
            loadingMatchesTeams: [],
            isLoading: false,
            isPassportModalOpen: false,
            passportItem: "",
            swapPlayer: null,
            disabledPositions: [],
            allPositions: props.data.positions,
            overlayLoading: false,
        };

        this.componentName = dataPath.replace(/^\/+/g, "").toLowerCase();
        const isSquad = this.componentName.includes("squad");

        this.apiEntity = isSquad ? "dhbteams" : "dhbmatches";
        this.updateEntity = isSquad ? "allTeamPlayers" : "allMatchPlayers";
        this.submitData = this.debounceSubmit(this.submitData, submitDelay);
    }

    async componentDidMount() {
        const isMatchTable =
            ["/playersHome", "/playersAway"].indexOf(this.props.dataPath) >= 0;
        if (isMatchTable) {
            const teamId =
                this.props.dataPath.indexOf("Home") >= 0
                    ? this.props.data.homeTeam
                    : this.props.data.awayTeam;
            this.teamApiData = await this.getTeamData(teamId);

            const selectedPositions = this.initializeSelectedPositions(
                this.selectionStore.items,
                this.state.allPositions
            );

            this.setState({ disabledPositions: selectedPositions });
        }
    }

    componentDidUpdate() {
        let newIds = [...new Set(toJS(this.props.value))];
        const loadedIds = toJS(
            this.selectionStore.items.map((item) => item.id)
        );

        if (!Array.isArray(newIds)) {
            newIds = Object.values(newIds);
        }

        if (!equals(newIds, loadedIds)) {
            this.selectionStore.loadItems(newIds);
        }

        this.header(this.props);
    }

    componentWillUnmount() {
        this.changeSelectionDisposer();
        this.changeOptionsDisposer();
    }

    @action closeOverlay() {
        this.overlayOpen = false;
    }

    @action openOverlay() {
        this.overlayOpen = true;
    }

    @action handleOverlayOpen = () => {
        if (this.state.swapPlayer) {
            this.setState({
                swapPlayer: null,
            });
        }
        this.openOverlay();
    };

    @action handleOverlayClose = () => {
        this.closeOverlay();
    };

    handleContactUpdate = async (adding, deleting) => {
        let data = this.state.data;

        const teamType = this.props.dataPath.includes("Home")
            ? data.homeTeam
            : data.awayTeam;
        if (
            this.componentName.includes("players") ||
            this.componentName.includes("squad")
        ) {
            const playerUpdater = new PlayerUpdater(
                data,
                this.teamApiData,
                teamType,
                this.componentName
            );
            data = playerUpdater.updatePlayers(adding, deleting);
        }
        if (this.componentName.includes("staff")) {
            const staffUpdater = new StaffUpdater(data, teamType);
            data = staffUpdater.updateStaff(adding, deleting);
        }
        if (this.componentName.includes("umpires")) {
            const umpireUpdater = new UmpireUpdater(data);
            data = umpireUpdater.updateUmpires(adding, deleting);
        }

        await this.submitData(data, "overlay");
    };

    initializeSelectedPositions = (players, allPositions) => {
        const disabledPositions = [];

        players.forEach((player) => {
            const selectedPosition = allPositions.find(
                (position) => position.id === player.position
            );

            if (selectedPosition && selectedPosition.isUniquePerGame) {
                disabledPositions.push(player.position);
            }
        });

        return disabledPositions;
    };

    async getTeamData(id) {
        try {
            const data = await ResourceRequester.get("dhbteam", {
                id,
                locale: this.locale,
            });
            return data;
        } catch (e) {
            console.error(e.message);
        }
    }

    handleNewSelection(selectedItems, allTeamPlayers, allMatchPlayers) {
        const itemsArray = Array.isArray(selectedItems)
            ? selectedItems
            : toJS(selectedItems);

        const newSelection = itemsArray.map((item, index) => {
            const existingPlayer = allMatchPlayers[item.id];
            const isStarting = index < this.startingPlayers;
            return {
                ...item,
                isGoalkeeper: existingPlayer
                    ? existingPlayer?.isGoalkeeper
                    : allTeamPlayers[item.id]?.isGoalkeeper,
                isCaptain: existingPlayer
                    ? existingPlayer?.isCaptain
                    : allTeamPlayers[item.id]?.isCaptain,
                isSubstituteGoalkeeper: existingPlayer
                    ? existingPlayer.isSubstituteGoalkeeper
                    : allTeamPlayers[item.id]?.isSubstituteGoalkeeper,
                shirtNumber: existingPlayer
                    ? existingPlayer.number
                    : allTeamPlayers[item.id]?.number,
                isInCoreSquad: allTeamPlayers[item.id]?.core,
                isStartingPlayer: isStarting,
                ...(existingPlayer && {
                    position: existingPlayer.position,
                }),
                ...(!existingPlayer && {
                    teamPositions: allTeamPlayers[item.id]?.positions,
                }),
            };
        });
        return newSelection;
    }

    handleOverlayConfirm = async (selectedItems: Array<Object>) => {
        this.setState({
            overlayLoading: true,
        });
        const selection = toJS(selectedItems);
        const currentState = this.players;
        const deleting = currentState.filter(
            ({ id: idCurrent }) =>
                !selection.some(
                    ({ id: idSelected }) => idCurrent === idSelected
                )
        );
        const adding = selection.filter(
            ({ id: idSelected }) =>
                !currentState.some(
                    ({ id: idCurrent }) => idSelected === idCurrent
                )
        );

        if (deleting.length) {
            let newPositions = [...this.state.disabledPositions];
            let positionsChanged = false;
            deleting.forEach((player) => {
                if (newPositions.includes(player.position)) {
                    positionsChanged = true;
                    newPositions = newPositions.filter(
                        (pos) => pos !== player.position
                    );
                }
            });

            if (positionsChanged) {
                this.setState({
                    disabledPositions: newPositions,
                });
            }
        }

        let finalSelection = selectedItems;

        await this.handleContactUpdate(adding, deleting);
        if (this.teamApiData && adding) {
            const newSelection = this.handleNewSelection(
                selectedItems,
                this.teamApiData.allTeamPlayers,
                this.state.allMatchPlayers
            );

            finalSelection = newSelection;
        }

        this.selectionStore.set(finalSelection);
    };

    handleSorted = (oldItemIndex: number, newItemIndex: number) => {
        this.selectionStore.move(oldItemIndex, newItemIndex);
    };

    debounceSubmit = (fn, delay) => {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                fn(...args);
            }, delay);
        };
    };

    reorderItems = (data) => {
        const selectedPlayers = toJS(this.props.value);
        const { dataPath } = this.props;

        const playersKey =
            dataPath === "/playersHome"
                ? "playersHome"
                : dataPath === "/playersAway"
                ? "playersAway"
                : null;

        if (playersKey && !equals(selectedPlayers, data[playersKey])) {
            this.selectionStore.reorderItems(data[playersKey], this.players);
        }
    };

    submitData = (data, overlay) => {
        const {
            data: { id },
        } = this.props;

        if (!this.state.isLoading) {
            this.setState({
                isLoading: true,
            });
        }

        const url = `${window.location.origin}/admin/api/${this.apiEntity}/${id}`;

        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
        };
        return fetch(url, requestOptions)
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Network response was not ok");
                }
                return response.json();
            })
            .then((data) => {
                this.setState({
                    data: data,
                    allMatchPlayers: data.allMatchPlayers,
                    allTeamPlayers: data.allTeamPlayers,
                    loadingMatchesTeams: [],
                    isLoading: false,
                    ...(overlay ? { overlayLoading: false } : {}),
                });
                this.reorderItems(data);

                overlay && this.closeOverlay();
            })
            .catch((error) => {
                console.log(error.message);
                this.setState({
                    loadingMatchesTeams: [],
                    isLoading: false,
                    ...(overlay ? { overlayLoading: false } : {}),
                });

                overlay && this.closeOverlay();
            });
    };

    @action itemClickHandler = (value, id, prop, swapPlayer) => {
        const playerService = new TeamService(
            this.state,
            this.props,
            this.selectionStore,
            this.submitData,
            this.players
        );
        const previousPosition = playerService.updatePlayerProp(
            id,
            prop,
            value
        );

        if (
            [
                "isCaptain",
                "isCheckbox",
                "shirtNumber",
                "isStartingPlayer",
                "positions",
                "position",
            ].includes(prop)
        ) {
            const updatedPlayers = playerService.updateMatchPlayers(
                id,
                prop,
                value,
                swapPlayer,
                previousPosition
            );

            if (updatedPlayers === false) {
                console.warn(
                    `Failed to update player ${id}. Player data not found.`
                );
                return; // Exit early without updating state or submitting
            }

            if (prop === "position" || prop === "isCheckbox") {
                const position = playerService.updatePositions(
                    previousPosition,
                    value,
                    this.state.allPositions,
                    this.state.disabledPositions
                );

                if (!equals(position, this.state.disabledPositions)) {
                    this.setState((prevState) => ({
                        ...prevState,
                        disabledPositions: position,
                    }));
                }
            }

            this.setState({ [this.updateEntity]: updatedPlayers });

            this.setState((prevState) => ({
                ...prevState,
                loadingMatchesTeams: [
                    ...prevState.loadingMatchesTeams,
                    { id, prop },
                ],
            }));

            this.submitData({
                ...this.state,
                [this.updateEntity]: updatedPlayers,
                isSquadChange: true,
            });
        }

        if (prop === "role" || prop === "umpireRole") {
            const updatedData = playerService.handleRoleChange(id, prop, value);

            if (updatedData) {
                this.submitData(updatedData);
            }
        }
    };

    @action handleSwapClick = (player) => {
        if (!this.state.swapPlayer) {
            this.setState({
                swapPlayer: {
                    id: player.id,
                    isStartingPlayer: player.isStartingPlayer,
                },
            });
        } else if (this.state.swapPlayer.id === player.id) {
            this.setState({
                swapPlayer: null,
            });
        } else {
            if (
                this.state.swapPlayer.isStartingPlayer !==
                player.isStartingPlayer
            ) {
                const selectedPlayer = this.selectionStore.items.find(
                    (p) => p.id === this.state.swapPlayer.id
                );
                this.setState({
                    isLoading: true,
                });

                this.itemClickHandler(
                    !player.isStartingPlayer,
                    selectedPlayer.id,
                    "isStartingPlayer",
                    player.id
                );
            }
            this.setState({
                swapPlayer: null,
            });
        }
    };

    @action calculateUmpireCost = (id) => {
        const inputElements = Array.from(
            document.querySelectorAll(`.js-calculation-input-${id}`)
        );
        let total = "";
        if (inputElements.length) {
            const inputValues = inputElements.map(
                (el) => parseFloat(el.value) || 0
            );
            total = inputValues.reduce(
                (acc, currentValue) => acc + currentValue,
                0
            );
            document
                .querySelector(`.js-submit-btn-${id}`)
                .removeAttribute("disabled");
        }
        return parseFloat(total).toFixed(2);
    };

    @action submitCost = (el, id) => {
        if (!el.hasAttribute("disabled")) {
            const costs = {
                costOvernight: Number(
                    document.querySelector(
                        `.js-calculation-input-${id}[data-cost='costOvernight']`
                    ).value
                ),
                costFixed: Number(
                    document.querySelector(
                        `.js-calculation-input-${id}[data-cost='costFixed']`
                    ).value
                ),
                costFood: Number(
                    document.querySelector(
                        `.js-calculation-input-${id}[data-cost='costFood']`
                    ).value
                ),
                costPublicTransport: Number(
                    document.querySelector(
                        `.js-calculation-input-${id}[data-cost='costPublicTransport']`
                    ).value
                ),
                costPersonalTransport: Number(
                    document.querySelector(
                        `.js-calculation-input-${id}[data-cost='costPersonalTransport']`
                    ).value
                ),
            };
            const data = {
                umpireCosts: {
                    contact: id,
                    ...costs,
                },
                isUmpireCostsChange: true,
            };
            this.submitData(data);
        }
    };

    @action openPassportModalHandler = (item) => {
        const { passportId } = item;

        if (this.passportCache[passportId]) {
            this.setState({
                isPassportModalOpen: true,
                passportItem: this.passportCache[passportId],
            });
            return;
        }

        this.setState({
            isPassportModalOpen: true,
            passportItem: "",
        });

        ResourceRequester.getList("dhbpassports", {
            locale: this.locale,
            id: passportId,
        })
            .then((data) => {
                if (data) {
                    this.passportCache[passportId] = data;

                    this.setState({
                        passportItem: data,
                    });
                }
            })
            .catch((error) => {
                this.setState({
                    passportItem: {
                        error: error.statusText,
                    },
                });
                console.log(error);
            });
    };

    @action closePassportModalHandler = () => {
        this.setState({
            isPassportModalOpen: false,
            passportItem: "",
        });
    };

    renderMultiItemSelectionItems(
        items,
        itemDisabledCondition,
        displayProperties,
        columns
    ) {
        const { disabledIds } = this.props;

        const ite = items.map((item, index) => {
            const itemDisabled =
                disabledIds.includes(item.id) ||
                (!!itemDisabledCondition &&
                    jexl.evalSync(itemDisabledCondition, item));

            const itemColumnClass = classNames(
                multiSelectionStyles.itemColumn,
                {
                    [multiSelectionStyles.disabled]: itemDisabled,
                }
            );

            const { availableStaffRoles = {}, availableUmpireRoles = {} } =
                this.props.data;

            // one select field per table; this is error
            let additionalParams = [];
            if (availableStaffRoles.$mobx) {
                additionalParams["staffRoles"] =
                    availableStaffRoles.$mobx.values; //
            }

            if (availableUmpireRoles.$mobx) {
                additionalParams["umpireRoles"] =
                    availableUmpireRoles.$mobx.values;
            }

            // if(dataPath.indexOf('players') >= 0 && window.location.hash.indexOf('report') >= 0){
            //     MultiItemS = MultiItemSelection1;
            // }

            let listGridColumns = "";
            if (
                this.props.schemaOptions["component"]?.value ===
                "dhbplayer_match_report"
            ) {
                listGridColumns = "1fr 3fr 2fr 1fr 1fr 1fr";
            } else if (
                this.props.schemaOptions["component"]?.value ===
                "dhbstaff_match_report"
            ) {
                listGridColumns = "repeat(4, 1fr)";
            } else if (
                this.props.schemaOptions["component"]?.value ===
                "dhbumpire_costs"
            ) {
                listGridColumns = "1.3fr repeat(7, 1fr)";
            } else if (
                this.props.schemaOptions["component"]?.value ===
                "dhbumpire_selection_match"
            ) {
                listGridColumns = "1.3fr repeat(6, 1fr)";
            } else if (
                this.props.dataPath === "/playersAway" ||
                this.props.dataPath === "/playersHome"
            ) {
                listGridColumns = "1fr 2fr repeat(6, 1fr)";
            } else if (
                this.props.dataPath === "/staffHome" ||
                this.props.dataPath === "/staffAway"
            ) {
                listGridColumns = "repeat(5, 1fr)";
            } else if (
                this.props.dataPath === "/squad" ||
                this.props.dataPath === "/coreSquad"
            ) {
                listGridColumns = "1fr 2fr repeat(4, 1fr)";
            } else {
                listGridColumns = "1.5fr 1.5fr repeat(5, 1fr)";
            }

            additionalParams["loadingMatchesTeams"] =
                this.state.loadingMatchesTeams;
            additionalParams["isLoading"] = this.state.isLoading;

            return (
                <MultiItemRenderer
                    item={item}
                    index={index}
                    isDisabled={this.props.disabled}
                    itemColumnClass={itemColumnClass}
                    displayProperties={displayProperties}
                    columns={columns}
                    inputField={this.inputField}
                    additionalParams={additionalParams}
                    data={this.state.data}
                    openPassportModalHandler={this.openPassportModalHandler}
                    handleSwapClick={this.handleSwapClick}
                    handleItemClick={this.itemClickHandler}
                    handleSubmitCost={this.submitCost}
                    listGridColumns={listGridColumns}
                    component={this.props.schemaOptions["component"]?.value}
                    dataPath={this.props.dataPath}
                    translate={this.props.t}
                    customColumns={this.columnCustom}
                    allItems={items.length}
                    swapPlayer={this.state.swapPlayer}
                    positions={this.props?.data?.positions}
                    selectedPositions={this.state.disabledPositions}
                ></MultiItemRenderer>
            );
        });

        return ite;
    }

    render() {
        const { disabled, disabledIds, icon, label, schemaOptions } =
            this.props;

        const itemDisabledCondition = "";

        const { resource_key: resourceKey } = this.props.fieldTypeOptions;
        const {
            adapter,
            display_properties: displayProperties,
            list_key: listKey,
            overlay_title: overlayTitle,
        } = this.props.fieldTypeOptions.types.list_overlay;

        const { items, loading } = this.selectionStore;

        const locale =
            this.props?.formInspector?.locale || observable.box("en");
        const columns = displayProperties.length;

        const overlayTranslatedTitle = translate(overlayTitle);

        let it = [];
        if (items && items.$mobx.values.length > 0) it = items.$mobx.values;

        const rItems = this.renderMultiItemSelectionItems(
            it,
            itemDisabledCondition,
            displayProperties,
            columns
        );

        const noOfSelectedPlayer = (items && items.length) || 0;

        if (items === undefined) {
            return null;
        }

        let options = parametarExtractor(this.props);

        let MultiItemS = MultiItemSelection1;

        this.players = this.selectionStore.items.toJS();

        const isDisabled =
            schemaOptions["component"]?.value === "dhbumpire_costs" || disabled;

        return (
            <form>
                <MultiItemS
                    label={label}
                    leftButton={
                        isDisabled
                            ? { disabled: true }
                            : { icon, onClick: this.handleOverlayOpen }
                    }
                    loading={loading}
                    onItemsSorted={this.handleSorted}
                    sortable={false}
                    header={this.header}
                    component={schemaOptions["component"]?.value || ""}
                >
                    {rItems}
                </MultiItemS>

                <MultiListOverlay
                    confirmLoading={this.state.overlayLoading}
                    adapter={adapter}
                    disabledIds={disabledIds}
                    itemDisabledCondition={itemDisabledCondition}
                    resourceKey={resourceKey}
                    listKey={listKey}
                    locale={locale}
                    onClose={this.handleOverlayClose}
                    onConfirm={this.handleOverlayConfirm}
                    open={this.overlayOpen}
                    options={options}
                    preSelectedItems={items}
                    title={overlayTranslatedTitle}
                />
                {this.footer(
                    noOfSelectedPlayer,
                    this.props.data.minNominatedSquadPlayerNo ||
                        this.props.data.minNominatedCoreSquadPlayers,
                    this.props.data.maxNominatedSquadPlayerNo,
                    this.props.dataPath,
                    schemaOptions?.hideLegend?.value,
                    this.props.t,
                    this.props.data.minNominatedStaffMembers,
                    this.props.data.maxNominatedStaffMembers
                )}
                <Modal
                    isOpen={this.state.isPassportModalOpen}
                    closeHandler={this.closePassportModalHandler}
                    passportItem={this.state.passportItem}
                />
            </form>
        );
    }
}

export default withTranslation()(CustomList);
